import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { EditorPage } from './pages/EditorPage';
import { LoginPage } from './pages/LoginPage';
import { MarketingLandingPage } from './pages/MarketingLandingPage';
import { RootPage } from './pages/RootPage';
import { StudioPage } from './pages/StudioPage';
import { AdminPage } from './pages/AdminPage';
import { PricingPage } from './pages/PricingPage';
import { BillingPage } from './pages/BillingPage';
import { LegalPage } from './pages/LegalPage';
import { api } from './api/client';


/**
 * Route gate for /admin: only role === 'admin' gets in. Unauthenticated users
 * go to /login; authenticated non-admins are bounced home (no hint the panel
 * exists). The server independently 403s the admin endpoints, so this is UX,
 * not the security boundary.
 */
function AdminGuard() {
  const [state, setState] = useState<'checking' | 'admin' | 'denied' | 'anon'>('checking');
  useEffect(() => {
    api
      .me()
      .then((u) => setState(u.role === 'admin' ? 'admin' : 'denied'))
      .catch(() => setState('anon'));
  }, []);
  if (state === 'checking') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0d0e12', color: '#8b8f9c', fontFamily: 'Inter, system-ui, sans-serif' }}>
        Checking access…
      </div>
    );
  }
  if (state === 'anon') return <Navigate to="/login" replace />;
  if (state === 'denied') return <Navigate to="/" replace />;
  return <AdminPage />;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

const router = createBrowserRouter([
  { path: '/', element: <RootPage /> },
  { path: '/welcome', element: <MarketingLandingPage /> },
  { path: '/welcome-v2', element: <MarketingLandingPage /> },
  { path: '/login', element: <LoginPage /> },
  // The standalone /new page folded into a dialog on the dashboard; keep the URL
  // working by redirecting to the dashboard with the dialog open.
  { path: '/new', element: <Navigate to="/?new=1" replace /> },
  { path: '/editor', element: <EditorPage /> },
  { path: '/editor/:projectId', element: <EditorPage /> },
  { path: '/studio', element: <StudioPage /> },
  { path: '/studio/:projectId', element: <StudioPage /> },
  { path: '/admin', element: <AdminGuard /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/billing', element: <BillingPage /> },
  { path: '/terms', element: <LegalPage tab="terms" /> },
  { path: '/privacy', element: <LegalPage tab="privacy" /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
